import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';
import {
  fetchAllSuccessPlans,
  fetchSuccessPlanByIdV2,
  createSuccessPlanV2,
  editSuccessPlanV2,
  fetchSuccessPlanStatusV2,
  fetchSuccessPlanProgressLookupsV2,
  fetchSuccessPlanStatusLookupsV2,
  completeSuccessPlanV2,
  markSuccessPlanInprogressV2,
  duplicateSuccessPlanV2,
  loadAllSuccessPlans,
  archiveSuccessPlanV2,
  fetchSuccessPlanFilesV2,
} from '../routines/successPlanRoutines';
import LOOKUP_TYPES from '../../common/constants/SuccessPlanConstants';

const SUCCESS_PLAN_BASE_URL = `${envConfig.APIG_URL}success-plan`;

function* refreshSuccessPlanList() {
  yield put(fetchAllSuccessPlans.trigger({ usePrevPayload: true }));
  const { successPlan } = yield select((state) => state.successPlansV2);
  const { successPlanId } = successPlan?.data || {};
  if (successPlanId) {
    yield put(
      fetchSuccessPlanByIdV2.trigger({
        successPlanId,
      })
    );
  }
}

function* fetchSuccessPlanByIdSagaV2({ payload }) {
  try {
    const { successPlanId } = payload;
    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/${successPlanId}`;
    const response = yield call(request, url);
    yield put(fetchSuccessPlanByIdV2.success(response.data));
  } catch (error) {
    yield put(fetchSuccessPlanByIdV2.failure({ error }));
  } finally {
    yield put(fetchSuccessPlanByIdV2.fulfill());
  }
}

export async function validateSuccessPlanNameSaga(
  latestSuccessPlanName,
  entityData
) {
  try {
    const queryParams = new URLSearchParams({
      latestSuccessPlanName,
      level: entityData?.level,
      entityId: entityData?.entityId,
    });

    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/get-by-name?${queryParams}`;
    const response = await request(url);

    return response.data;
  } catch (error) {
    return null;
  }
}

function* createSuccessPlanSagaV2(actionData) {
  const { payload } = actionData?.payload;
  try {
    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan`;
    const {
      name,
      description,
      startDate,
      dueDate,
      ownerId,
      collaborators,
      statusId,
      progressId,
      entity,
      organizationId,
      accountId,
    } = payload;

    const config = {
      method: 'POST',
      payload: {
        name,
        description,
        dueDate: dueDate.format('YYYY-MM-DD'),
        startDate: startDate.format('YYYY-MM-DD'),
        ownerId,
        collaborators,
        statusId,
        progressId,
        entity,
        organizationId,
        accountId,
      },
    };
    const response = yield call(request, url, config);
    yield put(
      createSuccessPlanV2.success({
        ...response.data,
        successMessage: SUCCESS_MESSAGES.ADD_SUCCESS_PLAN,
      })
    );
    yield call(refreshSuccessPlanList);
  } catch (error) {
    yield put(createSuccessPlanV2.failure({ error }));
  } finally {
    yield put(createSuccessPlanV2.fulfill());
  }
}

function* editSuccessPlanSaga({ payload }) {
  try {
    const { successPlan, successPlanId } = payload;
    const {
      name,
      description,
      startDate,
      dueDate,
      ownerId,
      collaborators,
      statusId,
      progressId,
    } = successPlan;

    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/${successPlanId}`;

    const config = {
      method: 'PUT',
      payload: {
        name,
        description,
        dueDate: dueDate.format('YYYY-MM-DD'),
        startDate: startDate.format('YYYY-MM-DD'),
        ownerId,
        collaborators,
        statusId,
        progressId,
      },
    };
    const response = yield call(request, url, config);
    yield put(
      editSuccessPlanV2.success({
        ...response.data,
        successMessage: SUCCESS_MESSAGES.EDIT_SUCCESS_PLAN,
      })
    );
    yield put(
      fetchSuccessPlanByIdV2.trigger({
        successPlanId,
      })
    );
    yield call(refreshSuccessPlanList);
  } catch (error) {
    yield put(editSuccessPlanV2.failure({ error }));
  } finally {
    yield put(editSuccessPlanV2.fulfill());
  }
}

function* fetchSuccessPlanStatusSaga({ payload }) {
  try {
    const { successPlanId } = payload;
    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/${successPlanId}/status`;
    const response = yield call(request, url);
    yield put(fetchSuccessPlanStatusV2.success(response.data));
  } catch (error) {
    yield put(fetchSuccessPlanStatusV2.failure({ error }));
  } finally {
    yield put(fetchSuccessPlanStatusV2.fulfill());
  }
}

function* fetchSuccessPlanStatusLookupsSaga() {
  try {
    const url = `${SUCCESS_PLAN_BASE_URL}/lookup?type=${LOOKUP_TYPES.SUCCESS_PLAN_STATUS}`;
    const response = yield call(request, url);
    yield put(fetchSuccessPlanStatusLookupsV2.success(response.data));
  } catch (error) {
    yield put(fetchSuccessPlanStatusLookupsV2.failure({ error }));
  } finally {
    yield put(fetchSuccessPlanStatusLookupsV2.fulfill());
  }
}

function* fetchSuccessPlanProgressLookupsSaga() {
  try {
    const url = `${SUCCESS_PLAN_BASE_URL}/lookup?type=${LOOKUP_TYPES.SUCCESS_PLAN_PROGRESS}`;
    const response = yield call(request, url);
    yield put(fetchSuccessPlanProgressLookupsV2.success(response.data));
  } catch (error) {
    yield put(fetchSuccessPlanProgressLookupsV2.failure({ error }));
  } finally {
    yield put(fetchSuccessPlanProgressLookupsV2.fulfill());
  }
}

function* completeSuccessPlanSaga({ payload }) {
  try {
    const { successPlanId, completeAll, progressId } = payload;
    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/${successPlanId}/complete?complete_all=${!!completeAll}`;
    const response = yield call(request, url, {
      method: 'PUT',
      payload: { progressId },
    });

    yield put(
      completeSuccessPlanV2.success({
        ...response.data,
        successMessage: SUCCESS_MESSAGES.COMPLETE_SUCCESS_PLAN,
      })
    );
    yield put(
      fetchSuccessPlanByIdV2.trigger({
        successPlanId,
      })
    );
    yield call(refreshSuccessPlanList);
  } catch (error) {
    yield put(completeSuccessPlanV2.failure({ error }));
  } finally {
    yield put(completeSuccessPlanV2.fulfill());
  }
}

function* markSuccessPlanInprogressSaga({ payload }) {
  try {
    const { successPlanId } = payload;
    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/${successPlanId}/uncomplete`;
    const response = yield call(request, url, { method: 'PUT', payload: {} });

    yield put(
      markSuccessPlanInprogressV2.success({
        ...response.data,
        successMessage: SUCCESS_MESSAGES.IN_PROGRESS_SUCCESS_PLAN,
      })
    );
    yield put(
      fetchSuccessPlanByIdV2.trigger({
        successPlanId,
      })
    );
    yield call(refreshSuccessPlanList);
  } catch (error) {
    yield put(markSuccessPlanInprogressV2.failure({ error }));
  } finally {
    yield put(markSuccessPlanInprogressV2.fulfill());
  }
}

function* duplicateSuccessPlanSaga({ payload }) {
  try {
    const { successPlanId, level, accountId, organizationId, entityId } =
      payload;
    yield put(loadAllSuccessPlans.trigger());
    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/${successPlanId}/duplicate`;
    const config = {
      method: 'POST',
      payload: {
        target: {
          [`${[level?.toLowerCase()]}Id`]: !entityId
            ? accountId || organizationId
            : entityId,
          entity: level?.toUpperCase(),
        },
      },
    };
    const response = yield call(request, url, config);
    yield put(
      duplicateSuccessPlanV2.success({
        ...response.data,
        successMessage: SUCCESS_MESSAGES.DUPLICATE_SUCCESS_PLAN,
      })
    );
    yield call(refreshSuccessPlanList);
  } catch (error) {
    yield put(duplicateSuccessPlanV2.failure({ error }));
  } finally {
    yield put(duplicateSuccessPlanV2.fulfill());
  }
}

function* archiveSuccessPlanSaga({ payload }) {
  try {
    const { successPlanId } = payload;
    const url = `${SUCCESS_PLAN_BASE_URL}/success-plan/${successPlanId}/archive`;
    const response = yield call(request, url, { method: 'PATCH', payload: {} });

    yield put(
      archiveSuccessPlanV2.success({
        ...response.data,
        successMessage: SUCCESS_MESSAGES.ARCHIVE_SUCCESS_PLAN,
      })
    );
    yield call(refreshSuccessPlanList);
  } catch (error) {
    yield put(archiveSuccessPlanV2.failure({ error }));
  } finally {
    yield put(archiveSuccessPlanV2.fulfill());
  }
}

function* fetchSuccessPlanFilesSagaV2({ payload }) {
  try {
    const { level, entityId } = payload;
    const URL = `${envConfig.APIG_URL}core/files?level=${level}&parentIdentifier=${entityId}`;
    const response = yield call(request, URL);
    yield put(fetchSuccessPlanFilesV2.success(response));
  } catch (error) {
    yield put(fetchSuccessPlanFilesV2.failure({ error }));
  } finally {
    yield put(fetchSuccessPlanFilesV2.fulfill());
  }
}

export default function* root() {
  yield all([
    takeEvery(fetchSuccessPlanByIdV2.TRIGGER, fetchSuccessPlanByIdSagaV2),
    takeLatest(createSuccessPlanV2.TRIGGER, createSuccessPlanSagaV2),
    takeLatest(editSuccessPlanV2.TRIGGER, editSuccessPlanSaga),
    takeEvery(fetchSuccessPlanStatusV2.TRIGGER, fetchSuccessPlanStatusSaga),
    takeLatest(
      fetchSuccessPlanStatusLookupsV2.TRIGGER,
      fetchSuccessPlanStatusLookupsSaga
    ),
    takeLatest(
      fetchSuccessPlanProgressLookupsV2.TRIGGER,
      fetchSuccessPlanProgressLookupsSaga
    ),
    takeLatest(
      fetchSuccessPlanProgressLookupsV2.TRIGGER,
      fetchSuccessPlanProgressLookupsSaga
    ),
    takeLatest(completeSuccessPlanV2.TRIGGER, completeSuccessPlanSaga),
    takeLatest(
      markSuccessPlanInprogressV2.TRIGGER,
      markSuccessPlanInprogressSaga
    ),
    takeLatest(duplicateSuccessPlanV2.TRIGGER, duplicateSuccessPlanSaga),
    takeLatest(archiveSuccessPlanV2.TRIGGER, archiveSuccessPlanSaga),
    takeLatest(fetchSuccessPlanFilesV2.TRIGGER, fetchSuccessPlanFilesSagaV2),
  ]);
}
